// env.ts — jedno miejsce, w którym Control Center dowiaduje się, DO CZEGO jest podłączony.
//
// Zasada R5-15: aplikacja nigdy nie „zgaduje” środowiska. Nazwa środowiska i pochodzenie
// danych są jawne, widoczne w UI i raportowane w każdym artefakcie. Dashboard, który nie
// potrafi powiedzieć, skąd ma liczby, jest teatrem — nie narzędziem decyzyjnym.

export type ApiSystem = 'crm' | 'drive' | 'main';

/** Klasa środowiska. Steruje bannerem i twardymi bezpiecznikami. */
export type EnvironmentKind = 'local' | 'test' | 'prod' | 'unknown';

/**
 * Pochodzenie danych pokazywanych w UI. Wymagane przez R5-15 §49 (DEFINITION OF REAL DATA).
 * - `controlled-test-dataset` — realny kod API + realny schemat DB, dane syntetyczne (kontrolowane).
 * - `test-environment`        — realne środowisko TEST TuSzamy.
 * - `production`              — dane produkcyjne (tylko odczyt, wymaga osobnej zgody właściciela).
 * - `unconfigured`            — brak podłączenia; UI MUSI pokazać pustkę, nie zmyślone liczby.
 */
export type DataOrigin = 'controlled-test-dataset' | 'test-environment' | 'production' | 'unconfigured';

export interface AppConfig {
  environment: EnvironmentKind;
  dataOrigin: DataOrigin;
  /** Bazowe adresy API per system. Zmiana z DIRECT_MULTI_API na BFF = zmiana tych trzech wartości. */
  apiBase: Record<ApiSystem, string>;
  /** Twardy bezpiecznik: w tej fazie Control Center jest read-only wobec każdego backendu. */
  readOnly: boolean;
}

function readEnv(key: string): string | undefined {
  const bag = import.meta.env as unknown as Record<string, string | undefined>;
  const v = bag[key];
  return v && v.length > 0 ? v : undefined;
}

function parseEnvironment(raw: string | undefined): EnvironmentKind {
  switch (raw) {
    case 'local':
    case 'test':
    case 'prod':
      return raw;
    default:
      return 'unknown';
  }
}

function parseDataOrigin(raw: string | undefined): DataOrigin {
  switch (raw) {
    case 'controlled-test-dataset':
    case 'test-environment':
    case 'production':
      return raw;
    default:
      return 'unconfigured';
  }
}

export function buildConfig(): AppConfig {
  const environment = parseEnvironment(readEnv('VITE_CC_ENV'));
  const dataOrigin = parseDataOrigin(readEnv('VITE_CC_DATA_ORIGIN'));

  return {
    environment,
    dataOrigin,
    // JEDNO WEJŚCIE. Przeglądarka nie chodzi już do CRM ani do Drive — chodzi wyłącznie do
    // backendu Control Center, a ten sięga do źródeł tożsamością serwisową. Trzy klucze zostają,
    // bo ekrany dalej mówią, o KTÓRE źródło pytają; wskazują natomiast to samo miejsce.
    //
    // Domyślnie puste, czyli TO SAMO POCHODZENIE co aplikacja. To nie jest wygoda: ciasteczko
    // sesji jest httpOnly i SameSite=Lax, więc rozjazd pochodzeń oznaczałby, że sesja przestaje
    // jechać z żądaniem — a wtedy jedyną alternatywą byłoby trzymanie tokena w JavaScripcie,
    // czyli powrót do sesji jako danych aplikacji.
    apiBase: (() => {
      const wejscie = readEnv('VITE_CC_API') ?? '';
      return { crm: wejscie, drive: wejscie, main: wejscie };
    })(),
    // R5-15-001 nie wprowadza ŻADNEGO write-flow. Pierwszy write wymaga AUDIT PASS (§23).
    readOnly: true,
  };
}

export const config: AppConfig = buildConfig();

/** Etykieta dla użytkownika — świadomie po polsku, świadomie bez upiększeń. */
export function describeDataOrigin(origin: DataOrigin): string {
  switch (origin) {
    case 'controlled-test-dataset':
      return 'Dane kontrolowane (realne API + realny schemat, dane syntetyczne)';
    case 'test-environment':
      return 'Środowisko TEST TuSzamy';
    case 'production':
      return 'Dane produkcyjne (tylko odczyt)';
    case 'unconfigured':
      return 'Brak podłączonego źródła danych';
  }
}
